/**
 * What an outdoor level is made of that a house is not: the hedges the player walks
 * between, and the moat around them.
 *
 * Three things live here and nothing else does, because the rest is already handled by
 * code that never had to learn about mazes. The grass is a `FloorSurface` like any room's
 * floor, so `LevelGeometry` draws it with the same `surfaceMaterial` call. The colliders —
 * hedges *and* the invisible bank barriers — come out of `collidableBoxes`, so they join
 * the one fixed rigid body the house's walls already share. The bank is the reason
 * barriers are drawn nowhere in this file: they are a wall the player meets and never
 * sees, and the water's edge is what has to read as the stop.
 *
 * Hedges are one draw call — a single `Instances` sharing one geometry, the idiom
 * `RoomTrim` uses for the house's mouldings. That geometry is no longer a unit box: it is
 * a lightly subdivided one dented inward by seeded noise (`hedgeGeometry`), which is what
 * takes the ruler off the silhouette without costing a second draw. Because the shape is
 * shared, the only per-piece variety available is the transform, so each piece is turned a
 * seeded half-turn about its own axis; and because the shape pins its ends flat, every
 * piece is oriented with the geometry's local +x along the run, whichever way the run
 * actually points. They cast and receive like walls do, for the same reason walls do: a
 * hedge that does not cast is a solid object the sun shines straight through.
 *
 * The water is the one thing out here that moves. Its texture offset is advanced in
 * `useFrame` through the texture object itself — no state, no store, exactly the technique
 * the rain shells in `ExteriorWorld` use. Movement is not decoration: the visual-style
 * spec asks that water read as water "by more than color alone", and a still puddle at the
 * bottom of a moat reads as painted concrete.
 */

import { Instance, Instances } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useMemo } from 'react';
import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';
import type { BuiltGrounds } from '../game/profile/buildGeometry';
import type { Box } from '../game/profile/types';
import { mulberry32, seedFromString } from '../utils/seededRandom';
import { boxPosition } from './boxUnits';
import { hedgeGeometry } from './hedgeGeometry';
import { Ladders } from './Ladders';
import { hedgeSegments } from './hedgeSegments';
import { getGradientMap } from './materials';
import { hedgeBumpCanvas, hedgeFaceCanvas, surfaceCanvas } from './textures';
import { useDisposable } from './useDisposable';

type Props = {
  grounds: BuiltGrounds;
};

/**
 * Hedge and water colours live here rather than in the profile palette, deliberately.
 * The palette describes a *house* — floor, walls, ceiling, trim — and a maze has none of
 * those; giving grounds their own palette block would mean a profile could author a pink
 * hedge and a lava moat with nothing to stop it. These are what a hedge and a moat are.
 */
const HEDGE_COLOR = '#3e7a4c';
const WATER_COLOR = '#24506b';

/**
 * World metres covered by one tile of the water pattern — cf. `FLOOR_TILE` in
 * `LevelGeometry`. The hedges need no such number: `hedgeSegments` has already cut every
 * run to about one cell, so one tile per piece *is* a fixed metre scale.
 */
const WATER_TILE = 4.5;

/**
 * Drift, in texture tiles per second. One tile is `WATER_TILE` metres on every panel
 * however big it is (the sampled coordinate is `uv * repeat + offset`, so an offset step
 * moves the pattern by whole tiles, not by a fraction of the panel), which is what makes
 * the same numbers give the same *world* speed on the long panels and the short ones —
 * the moat has one current, not four.
 *
 * ~9 cm/s along the moat with a slow sideways set. Faster than this and it stops being
 * water and becomes a conveyor belt.
 */
const DRIFT_ALONG = 0.02;
const DRIFT_ACROSS = 0.006;

/**
 * How deep the moon appears to bite into the leaf clumps.
 *
 * A bump map perturbs the normal, and a toon material turns the normal into one of five
 * flat bands — so this does not shade the hedge smoothly, it decides which clumps fall
 * into the next band down. Small numbers do nothing at all and large ones make the bands
 * boil into speckle; this is about as far as it can go before the hedge stops reading as
 * one surface. Unverified on a handheld, like everything else about this level's look.
 */
const HEDGE_BUMP_SCALE = 0.45;

function tiled(canvas: HTMLCanvasElement, repeatX: number, repeatY: number): CanvasTexture {
  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.repeat.set(Math.max(1, repeatX), Math.max(1, repeatY));
  return texture;
}

/** How a single hedge piece is placed: see the orientation note in the file header. */
type Placement = {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
};

/**
 * Turns the drawn pieces into instance transforms.
 *
 * Two things happen here that the geometry cannot do for itself. Every piece is rotated so
 * the shared geometry's local +x lies along the hedge run — the shape pins its ±x faces
 * flat precisely so that pieces of one run butt together without a notch, and that is only
 * true if "along the run" is always the same local axis. And each piece is given a seeded
 * half-turn on top, so the same lump pattern does not march down a 36 m wall six times in a
 * row. A rotation rather than a negative scale: mirroring flips triangle winding, and the
 * fix for that is a second material with `side` reversed, which is a second draw call.
 */
function placements(pieces: readonly Box[]): Placement[] {
  const random = mulberry32(seedFromString('hedge-instances'));

  return pieces.map((piece) => {
    const alongX = piece.size.x >= piece.size.z;
    const run = alongX ? piece.size.x : piece.size.z;
    const through = alongX ? piece.size.z : piece.size.x;
    const flipped = random() < 0.5;

    return {
      position: boxPosition(piece),
      rotation: [0, alongX ? (flipped ? Math.PI : 0) : flipped ? -Math.PI / 2 : Math.PI / 2, 0],
      scale: [run, piece.size.y, through],
    };
  });
}

export function MazeGrounds({ grounds }: Props) {
  // Memoized where it used to be recomputed every render, because the instance transforms
  // below are derived from it through a seeded generator: rebuilding the list would
  // reshuffle which pieces are flipped, and the maze would twitch on every re-render.
  const pieces = useMemo(() => hedgeSegments(grounds.hedges), [grounds]);
  const hedgeShape = useMemo(() => hedgeGeometry(), []);
  useDisposable(hedgeShape);
  const hedgePlacements = useMemo(() => placements(pieces), [pieces]);

  // useMemo for the same reason `ExteriorWorld` uses it: these are GPU resources, and the
  // water textures in particular have to be *the same objects* across renders or the
  // per-frame offset below is written to one that has already been replaced.
  const hedgeMap = useMemo(() => {
    // `hedgeFaceCanvas` rather than the plain `'hedge'` pattern the grass uses: it is the
    // same leaves with the footing shadow baked down it, which is only correct at one tile
    // per hedge.
    const canvas = hedgeFaceCanvas(HEDGE_COLOR);
    if (!canvas) return null;
    // One tile per drawn piece: `hedgeSegments` has already cut every run to about
    // `HEDGE_SEGMENT` metres, so a fixed repeat is a near-fixed leaf size everywhere. It is
    // also what puts the baked footing shadow at the hedge's actual footing — v runs the
    // full height of a piece exactly once.
    return tiled(canvas, 1, 1);
  }, []);
  useDisposable(hedgeMap);

  /**
   * The height field for the same leaf clumps. No `colorSpace`, unlike the maps above:
   * `CanvasTexture` defaults to none, which is right for data — an sRGB decode here would
   * bend every slope. The default 1×1 repeat is left alone because it has to match the
   * colour map's, or the relief slides off the leaves it belongs to.
   */
  const hedgeBump = useMemo(() => {
    const canvas = hedgeBumpCanvas();
    if (!canvas) return null;
    const texture = new CanvasTexture(canvas);
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    return texture;
  }, []);
  useDisposable(hedgeBump);

  const waterMaps = useMemo(() => {
    const canvas = surfaceCanvas('water', WATER_COLOR);
    if (!canvas) return null;
    return grounds.waterPanels.map((panel) =>
      tiled(canvas, panel.size.x / WATER_TILE, panel.size.z / WATER_TILE),
    );
  }, [grounds]);
  useDisposable(waterMaps);

  useFrame((_, delta) => {
    if (!waterMaps) return;
    // Wrapped by hand rather than left to climb: an offset growing forever loses float
    // precision and the ripples visibly judder after a long session (see `ExteriorWorld`).
    for (const map of waterMaps) {
      map.offset.y = (map.offset.y + delta * DRIFT_ALONG) % 1;
      map.offset.x = (map.offset.x + delta * DRIFT_ACROSS) % 1;
    }
  });

  return (
    <>
      {hedgePlacements.length > 0 && (
        /* frustumCulled off: three.js culls an instanced mesh by its BASE geometry's
           bounding sphere — a one-metre blob at the world origin — not by where the ~170
           instances stand. Turn until the origin leaves the frustum and every hedge in
           the maze vanishes at once. One always-issued draw is the entire cost. */
        <Instances
          frustumCulled={false}
          limit={hedgePlacements.length}
          range={hedgePlacements.length}
          geometry={hedgeShape}
          castShadow
          receiveShadow
        >
          {/* The pattern already carries the colour, so the base stays white and leaves it
              unmultiplied — the same bargain `surfaceMaterial` makes. Without a canvas
              (tests, SSR) the colour has to come back or the hedges render white. The bump
              map is the other half of the relief the lumpy geometry starts: the silhouette
              says where the hedge ends, this says where its clumps catch the moon. */}
          <meshToonMaterial
            map={hedgeMap}
            bumpMap={hedgeBump}
            bumpScale={HEDGE_BUMP_SCALE}
            color={hedgeMap ? '#ffffff' : HEDGE_COLOR}
            gradientMap={getGradientMap()}
          />
          {hedgePlacements.map((placement, index) => (
            <Instance
              key={`hedge-${index}`}
              position={placement.position}
              rotation={placement.rotation}
              scale={placement.scale}
            />
          ))}
        </Instances>
      )}

      <Ladders ladders={grounds.ladders} />

      {/* Opaque, and that is a choice rather than an oversight. There is no bed under the
          moat to see through to — the water is the only surface down there — so
          transparency would show the black exterior ground beneath it and put four more
          meshes into the back-to-front sort for the privilege. The ripple bands and the
          drift above are what make it read as water. */}
      {grounds.waterPanels.map((panel, index) => (
        <mesh key={`water-${index}`} position={boxPosition(panel)}>
          <boxGeometry args={[panel.size.x, panel.size.y, panel.size.z]} />
          <meshToonMaterial
            map={waterMaps?.[index] ?? null}
            color={waterMaps ? '#ffffff' : WATER_COLOR}
            gradientMap={getGradientMap()}
          />
        </mesh>
      ))}
    </>
  );
}
